import React from 'react';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import { createFontAwesomeIcon } from '../../../services/iconUtils';
import ReactDOMServer from 'react-dom/server';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';


interface StockData {
  symbol: string;
  market_symbol: string;
  market_name: string;
  exchange_symbol: string;
  exchange_name: string;
  trading_date: string;
  open: number | null;
  open_yield: number | null;
  high: number | null;
  high_yield: number | null;
  low: number | null;
  low_yield: number | null;
  close: number;
  official_last: number;
  official_last_corrected: number;
  official_last_yield_corrected: number | null;
  close_yield: number | null;
  volume: number;
  turnover: number;
  number_of_trades: number;
  number_of_alerts: number | null;
}

interface StockChartProps {
  data: StockData[];
}

const StockChart: React.FC<StockChartProps> = ({ data }) => {
  // Transform data into candlestick format: [timestamp, open, high, low, close]

  const seriesDataV2 = data.map(item => {
    const timestamp = new Date(item.trading_date).getTime();
    const point: { x: number; open: number | undefined; high: number | undefined; low: number | undefined; close: number; lineColor?: string } = {
      x: timestamp,
      open: item.open !== null ? item.open : item.close,
      high: item.high !== null ? item.high : item.close,
      low: item.low !== null ? item.low : item.close,
      close: item.close
    };

    // Set lineColor to green if open and close are the same and not null.
    if (item.open !== null && item.close !== null && item.open === item.close) {
      point.lineColor = '#01B1A7'; // green color
    }

    // Check if open is not null and equals close, then set lineColor to gray.
    if (item.open === null && item.high === null && item.low === null) {
      point.lineColor = 'gray';
    }

    return point;
  });

  // Create alert data for items with alerts.
  // The marker is placed slightly above the high price.
  const alertData = data
    .filter(item => item.number_of_alerts !== null && item.number_of_alerts > 0)
    .map(item => ({
      x: new Date(item.trading_date).getTime(),
      y: item.high !== null ? item.high + 0.5 : item.close,
      alerts: item.number_of_alerts
    }));

  const bellIcon = ReactDOMServer.renderToString(<FontAwesomeIcon icon={faBell} size='1x' />);

  // A base64-encoded SVG icon (a simple red exclamation mark).
  const alertIconUrl ='data:image/svg+xml;base64,' + btoa(createFontAwesomeIcon(bellIcon, "#FFB31F", 13));

  const volumeSeriesData = data.map(item => [
    new Date(item.trading_date).getTime(),
    item.volume
  ]);

  const options: Highcharts.Options = {
    plotOptions: {
      candlestick: {
        color: '#EA5F5F',   // color for down candles
        upColor: '#01B1A7', // color for up candles
        lineColor: '#EA5F5F',
        upLineColor: '#01B1A7',
        // You can also set borderColor, etc.
      }
    },
    rangeSelector: {
      selected: 5
    },
    xAxis: {
      type: 'datetime'
    },
    yAxis: [
      {
        title: { text: 'Price' },
        height: '70%',
        lineWidth: 2
      },
      {
        title: { text: 'Volume' },
        top: '75%',
        height: '25%',
        offset: 0,
        lineWidth: 2
      }
    ],
    tooltip: {
      shared: true,
      useHTML: true,
      formatter: function() {
        let symbol = this.points?.find((point: any) => point.series.type === 'candlestick')?.series.name || '';
        let dateStr = this.x ? Highcharts.dateFormat('%A, %b %e, %Y', this.x) : '';
        let s = `<b>${symbol}</b><br/><span style="font-size: 13px; color: #292A2E;">${dateStr}</span><br/>`;
        let price, volume;
        if (this.points) {
          this.points.forEach(function(point: any) {
            if (point.series.type === 'candlestick') {
              price = point.point;
              s += `Open: ${price.open}<br/>High: ${price.high}<br/>Low: ${price.low}<br/>Close: ${price.close}<br/>`;
            }
            if (point.series.name === 'Volume') {
              volume = point.y;
              s += `Volume: ${volume}<br/>`;
            }
          });
        }
        return s;
      }
    },
    series: [
      {
        type: 'candlestick',
        name: data[0]?.symbol,
        data: seriesDataV2,
        tooltip: {
          valueDecimals: 3
        }
      },
      {
        // Add a scatter series to show alerts as icons.
        type: 'scatter',
        name: 'Alerts',
        data: alertData,
        tooltip: {
          pointFormat: 'Alerts: {point.alerts}'
        },
        marker: {
          symbol: 'url(' + alertIconUrl + ')',
          width: 18,
          height: 18
        }
      },
      {
        type: 'column',
        name: 'Volume',
        data: volumeSeriesData,
        yAxis: 1,
        color: 'black',
        tooltip: {
          valueDecimals: 0
        }
      }
    ]
  };

  return (
    <div>
      <HighchartsReact
        highcharts={Highcharts}
        constructorType={'stockChart'}
        options={options}
      />
    </div>
  );
};

export default StockChart;
